import { useState, useCallback } from 'react';
import axios from 'axios';
import Cookies from 'js-cookie';

/**
 * Custom hook for Referral API integration
 * Provides methods to interact with the referral system endpoints
 */
export const useReferralApi = () => {
  const apiDomain = process.env.REACT_APP_API_DOMAIN;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Get auth headers
  const getAuthHeaders = useCallback(() => {
    const accessToken = Cookies.get("fbauthToken") || Cookies.get("authToken");
    return {
      'Content-Type': 'application/json',
      ...(accessToken && { Authorization: `Bearer ${accessToken}` }),
    };
  }, []);

  // ============================================================
  // ADMIN ENDPOINTS
  // ============================================================

  /**
   * Get referral statistics
   * GET /api/v2/referral/admin/stats
   */
  const getStats = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(
        `${apiDomain}/api/v2/referral/admin/stats`,
        { headers: getAuthHeaders() }
      );
      return response.data;
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to fetch stats');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [apiDomain, getAuthHeaders]);

  /**
   * Get current referral percentage
   * GET /api/v2/referral/admin/get-percentage
   */
  const getPercentage = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(
        `${apiDomain}/api/v2/referral/admin/get-percentage`,
        { headers: getAuthHeaders() }
      );
      return response.data;
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to fetch percentage');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [apiDomain, getAuthHeaders]);

  /**
   * Set referral percentage
   * POST /api/v2/referral/admin/set-percentage
   */
  const setPercentage = useCallback(async (percentage, secondLevel, adminId) => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.post(
        `${apiDomain}/api/v2/referral/admin/set-percentage`,
        {
          referral_percentage: percentage,
          second_level_percentage: secondLevel,
          admin_id: adminId,
        },
        { headers: getAuthHeaders() }
      );
      return response.data;
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to set percentage');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [apiDomain, getAuthHeaders]);

  /**
   * Get all referral transactions (paginated)
   * GET /api/v2/referral/admin/all?page=1&limit=10
   */
  const getTransactions = useCallback(async (page = 1, limit = 10) => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(
        `${apiDomain}/api/v2/referral/admin/all`,
        {
          params: { page, limit },
          headers: getAuthHeaders(),
        }
      );
      return response.data;
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to fetch transactions');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [apiDomain, getAuthHeaders]);

  /**
   * Get all referral users (paginated)
   * GET /api/v2/referral/admin/users?page=1&limit=10
   */
  const getUsers = useCallback(async (page = 1, limit = 10) => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(
        `${apiDomain}/api/v2/referral/admin/users`,
        {
          params: { page, limit },
          headers: getAuthHeaders(),
        }
      );
      return response.data;
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to fetch users');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [apiDomain, getAuthHeaders]);

  /**
   * Get sponsors with referral activity (paginated)
   * GET /api/v2/referral/admin/sponsors-with-referrals?page=1&limit=10
   */
  const getSponsorsWithReferrals = useCallback(async (page = 1, limit = 10) => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(
        `${apiDomain}/api/v2/referral/admin/sponsors-with-referrals`,
        {
          params: { page, limit },
          headers: getAuthHeaders(),
        }
      );
      // Normalize the API shape ({ items, total }) to what the UI expects
      // ({ sponsors, total_count }). Each item maps total_referrals + the
      // chain so the cards render; username falls back to the numeric id.
      const d = response.data || {};
      const items = d.items || d.sponsors || [];
      return {
        sponsors: items.map((s) => ({
          ...s,
          user_id: s.sponsor_user_id || s.user_id,
          username: s.username || s.sponsor_numeric_id || s.sponsor_user_id,
          total_referrals: s.total_referrals ?? 0,
        })),
        total_count: d.total ?? d.total_count ?? items.length,
        page: d.page ?? page,
      };
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to fetch sponsors');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [apiDomain, getAuthHeaders]);

  // ============================================================
  // USER ENDPOINTS
  // ============================================================

  /**
   * Get user's referral chain
   * GET /api/v2/referral/chain?user_id=X
   */
  const getUserChain = useCallback(async (userId) => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(
        `${apiDomain}/api/v2/referral/chain`,
        {
          params: { user_id: userId },
          headers: getAuthHeaders(),
        }
      );
      return response.data;
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to fetch user chain');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [apiDomain, getAuthHeaders]);

  /**
   * Validate a referral code
   * GET /api/v2/referral/validate?code=X
   */
  const validateReferralCode = useCallback(async (code) => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(
        `${apiDomain}/api/v2/referral/validate`,
        {
          params: { code: code.toLowerCase() },
          headers: getAuthHeaders(),
        }
      );
      return response.data;
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to validate code');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [apiDomain, getAuthHeaders]);

  /**
   * Get user's referral info
   * GET /api/v2/referral/user-info?user_id=X
   */
  const getUserInfo = useCallback(async (userId) => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(
        `${apiDomain}/api/v2/referral/user-info`,
        {
          params: { user_id: userId },
          headers: getAuthHeaders(),
        }
      );
      return response.data;
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to fetch user info');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [apiDomain, getAuthHeaders]);

  /**
   * Get user's referral history
   * GET /api/v2/referral/history?user_id=X
   */
  const getUserHistory = useCallback(async (userId) => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(
        `${apiDomain}/api/v2/referral/history`,
        {
          params: { user_id: userId },
          headers: getAuthHeaders(),
        }
      );
      return response.data;
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to fetch history');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [apiDomain, getAuthHeaders]);

  /**
   * Get user's remaining balance
   * GET /api/v2/referral/remaining-reward?user_id=X
   */
  const getRemainingBalance = useCallback(async (userId) => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(
        `${apiDomain}/api/v2/referral/remaining-reward`,
        {
          params: { user_id: userId },
          headers: getAuthHeaders(),
        }
      );
      return response.data;
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to fetch balance');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [apiDomain, getAuthHeaders]);

  /**
   * Get user's rewards
   * GET /api/v2/referral/rewards?user_id=X
   */
  const getUserRewards = useCallback(async (userId) => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(
        `${apiDomain}/api/v2/referral/rewards`,
        {
          params: { user_id: userId },
          headers: getAuthHeaders(),
        }
      );
      return response.data;
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to fetch rewards');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [apiDomain, getAuthHeaders]);

  // ============================================================
  // LEGACY ENDPOINTS (for backward compatibility)
  // ============================================================

  /**
   * Set referral data (legacy)
   * POST /api/v2/referral-data
   */
  const setReferralData = useCallback(async (referrerReward, referredUserReward) => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.post(
        `${apiDomain}/api/v2/referral-data`,
        {
          referrer_reward: parseFloat(referrerReward),
          referred_user_reward: parseFloat(referredUserReward),
        },
        { headers: getAuthHeaders() }
      );
      return response.data;
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to set referral data');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [apiDomain, getAuthHeaders]);

  /**
   * Get referral data (legacy)
   * GET /api/v2/referral-data/get
   */
  const getReferralData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(
        `${apiDomain}/api/v2/referral-data/get`,
        { headers: getAuthHeaders() }
      );
      return response.data;
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to fetch referral data');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [apiDomain, getAuthHeaders]);

  return {
    // State
    loading,
    error,
    
    // Admin endpoints
    getStats,
    getPercentage,
    setPercentage,
    getTransactions,
    getUsers,
    getSponsorsWithReferrals,
    
    // User endpoints
    getUserChain,
    validateReferralCode,
    getUserInfo,
    getUserHistory,
    getRemainingBalance,
    getUserRewards,
    
    // Legacy endpoints
    setReferralData,
    getReferralData,
  };
};

export default useReferralApi;
